import {
    AppState,
    AuthenticationState,
    THEWEST_ALL,
} from '@news-mono/web-common'
import { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
    BreachContext,
    BreachScreenContext,
} from '../../../../web-common/src/context-providers/BreachContextProvider'
import { BreachWallContentType } from './components/BreachNotificationWrapper/BreachNotificationWrapper'

export interface ISubscriptionPackage {
    pretitle: string
    originalPrice?: string
    heading: string
    price: string
    priceQualifier?: string
    ribbonTitle?: string
    packagePath: string
    offerCode?: string
    packageType?: 'digital' | 'digitalprint'
    packageTheme?: 'default' | 'red' | 'yellow'
    cancellationMessage?: string
    googleSKU?: string
    // Properties for ga4 event - ACQ-17
    weeklyPrice: number
    weeklyDiscount?: number
    rateCode: string
    benefitOverride?: string[]
}

export type SubscriptionPackages = ISubscriptionPackage[]

export const WEEKLY_DIGITAL_RATE_CODE = 'WST_DIGI_DD'
export const WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE = 'WST_PrSS+DIGI_DD'
export const FIFTY_OFF_EVERYDAY_DIGITAL_12_WEEKS_RATE_CODE = 'WST_DIGI$3.50x12_DD'
export const FIFTY_OFF_FRI_MON_PAPER_AND_DIGITAL_12_WEEKS_RATE_CODE = 'WST-M---FSS+DIG_6.5'
export const BASE_50_DEAL_OFFERCODE = 'DIGDIS21'

const THIRTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE = 'WST_DIGI$4.90x8_DD'

export const EVERYDAY_DIGITAL = 7
export const WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL = 9
export const FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL = 13
export const FIFTY_OFF_EVERYDAY_DIGITAL = EVERYDAY_DIGITAL * .50
export const FIFTY_OFF_WE_PAPERS_AND_EVERYDAY_DIGITAL = WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL * .50
const FIFTY_OFF_FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL = FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL * .50
const THIRTY_OFF_EVERYDAY_DIGITAL = EVERYDAY_DIGITAL * .70
const THIRTY_OFF_WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL = WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL * .70

const base50percentDiscountPackage: ISubscriptionPackage[] = [
    {
        packageTheme: 'red',
        packagePath: 'subscribe/new',
        pretitle: 'Digital',
        heading: 'Everyday Digital',
        price: '50c per day',
        originalPrice: '$1 per day',
        priceQualifier: 'for 8 weeks*',
        packageType: 'digital',
        ribbonTitle: '50% off',
        offerCode: BASE_50_DEAL_OFFERCODE,
        cancellationMessage: 'Cancel anytime. Min cost $14.',
        weeklyPrice: FIFTY_OFF_EVERYDAY_DIGITAL,
        weeklyDiscount: 3.5,
        rateCode: 'WST_DIGI_$3.50X8W',
    },
    {
        pretitle: 'Digital & Print',
        heading: 'Weekend Papers + Everyday Digital',
        price: '$9 per week',
        packagePath: 'subscribenow/sat-sun+digital',
        packageType: 'digitalprint',
        cancellationMessage: 'Cancel anytime. Min cost $36.',
        weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
        weeklyDiscount: 0,
        rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
    },
]

export const breachPackages: Record<
    BreachWallContentType,
    ISubscriptionPackage[]
> = {
    default: [
        {
            packagePath: 'subscribe/new',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: '$1 per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            googleSKU: 'everyday_digital_4weeks',
            weeklyPrice: EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WEEKLY_DIGITAL_RATE_CODE,
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. Min cost $36.',
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
        },
    ],
    'digital-only': [
        {
            packagePath: 'subscribe/new',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: '$1 per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            googleSKU: 'everyday_digital_4weeks',
            weeklyPrice: EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WEEKLY_DIGITAL_RATE_CODE,
        },
    ],
    'top-50-schools': [
        {
            packageTheme: 'yellow',
            packagePath: 'offercheck/thewest?redirect=true&offerCode=SCHOOL30',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: 'Now $4.90 per week',
            originalPrice: 'Was $7 per week',
            priceQualifier: 'for the first 8 weeks*',
            packageType: 'digital',
            ribbonTitle: '30% off*',
            offerCode: 'SCHOOL30',
            cancellationMessage: 'Cancel anytime. 4 week minimum.',
            weeklyPrice: THIRTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 2.1,
            rateCode: THIRTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. 4 week minimum.',
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
        },
    ],
    '30-off-breach-discount-biked130': [
        {
            packageTheme: 'yellow',
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BIKEDI30',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: '$4.90 per week',
            originalPrice: '$7 per week',
            priceQualifier: 'for the first 8 weeks*',
            packageType: 'digital',
            ribbonTitle: '30% off*',
            rateCode: THIRTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
            weeklyPrice: THIRTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 2.1,
            offerCode: 'BIKEDI30',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. Min cost $36.',
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
        },
    ],
    '50-off-afl-breach-screen': [
        {
            packagePath: 'offercheck/thewest?redirect=true&offerCode=FTDIG50',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: '$3.50 per week',
            originalPrice: '$7 per week',
            priceQualifier: 'for the first 12 weeks*',
            packageType: 'digital',
            ribbonTitle: '50% off',
            rateCode: FIFTY_OFF_EVERYDAY_DIGITAL_12_WEEKS_RATE_CODE,
            weeklyPrice: FIFTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 3.5,
            offerCode: 'FTDIG50',
            googleSKU: 'everyday_digital_footy_pack',
            cancellationMessage: 'Cancel anytime. 4 week minimum.',
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Fri-Mon Papers + Everyday Digital',
            price: '$6.50 per week',
            originalPrice: '$13 per week',
            priceQualifier: 'for the first 12 weeks*',
            benefitOverride: [
                'Access to all digital content',
                'Fri-Mon Papers delivered',
                'Regional online news',
                'The West App',
                'Daily digital edition newspaper',
                'Subscriber rewards',
            ],
            packagePath: 'offercheck/thewest?redirect=true&offerCode=FTPRI50',
            packageType: 'digitalprint',
            ribbonTitle: '50% off',
            cancellationMessage: 'Cancel anytime. 4 week minimum.',
            rateCode: FIFTY_OFF_FRI_MON_PAPER_AND_DIGITAL_12_WEEKS_RATE_CODE,
            weeklyPrice: FIFTY_OFF_FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 6.5,
            offerCode: 'FTPRI50',
        },
    ],
    'black-friday': [
        {
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BLKFRI1',
            offerCode: 'BLKFRI1',
            pretitle: 'Digital',
            originalPrice: '$1 a day',
            heading: 'Everyday Digital',
            price: '50c per day',
            priceQualifier: 'for 8 weeks*',
            packageType: 'digital',
            weeklyPrice: FIFTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 3.5,
            rateCode: 'WST_DIGI_$4.20X8W_DD',
        },
        {
            ribbonTitle: 'Most popular',
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            originalPrice: '$9 per week',
            price: '$4.50 per week',
            priceQualifier: 'for 8 weeks*',
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BLKFRI2',
            offerCode: 'BLKFRI2',
            packageType: 'digitalprint',
            weeklyPrice: FIFTY_OFF_WE_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 4.5,
            rateCode: 'WST_SS+DIG4.50X8W_DD',
        },
    ],
    '50-off-breach-discount-coviddig': base50percentDiscountPackage,
    '50-off-breach-discount-govan50': base50percentDiscountPackage,
    '50-off-breach-discount': base50percentDiscountPackage,
    eofy: [
        {
            packagePath: 'subscribe/new',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: '$1 per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            googleSKU: 'everyday_digital_4weeks',
            weeklyPrice: EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WEEKLY_DIGITAL_RATE_CODE,
        },
        {
            ribbonTitle: '30% OFF*',
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            originalPrice: '$9 per week',
            price: '$6.30 per week',
            priceQualifier: 'for 8 weeks*',
            packagePath: 'offercheck/thewest',
            offerCode: 'EOFY6',
            packageType: 'digitalprint',
            weeklyPrice: THIRTY_OFF_WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 2.7,
            rateCode: 'WST_PrSSDIG6.3x8_DD',
        },
    ],
    'business-pack': [
        {
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BUSN2',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            originalPrice: '$1 per day',
            price: '$0.70  per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            ribbonTitle: '30% OFF*',
            priceQualifier: 'for the first 8 weeks*',
            offerCode: 'BUSN2',
            weeklyPrice: THIRTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 2.1,
            rateCode: THIRTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. Min cost $36.',
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
        },
    ],
    'sport-pack': [
        {
            packagePath: 'offercheck/thewest?redirect=true&offerCode=SP3022',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            originalPrice: '$1 per day',
            price: '$0.70  per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            ribbonTitle: '30% OFF*',
            priceQualifier: 'for the first 8 weeks*',
            offerCode: 'SP3022',
            weeklyPrice: THIRTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 2.1,
            rateCode: THIRTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. Min cost $36.',
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
        },
    ],
    'bali-bombing': [
        {
            packagePath: 'offercheck/thewest?redirect=true&offerCode=BB2022',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            originalPrice: '$1 per day',
            price: '$0.70  per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            ribbonTitle: '30% OFF*',
            priceQualifier: 'for the first 8 weeks*',
            offerCode: 'BB2022',
            weeklyPrice: THIRTY_OFF_EVERYDAY_DIGITAL,
            weeklyDiscount: 2.1,
            rateCode: THIRTY_OFF_EVERYDAY_DIGITAL_8_WEEKS_RATE_CODE,
        },
        {
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. Min cost $36.',
            weeklyPrice: WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL,
            weeklyDiscount: 0,
            rateCode: WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE,
        },
    ],
    sport: [],
}


export function packageOfferDetails(
    type: BreachWallContentType,
): SubscriptionPackages {
    const packages: SubscriptionPackages = []

    switch (type) {
        case '50-off-breach-discount-coviddig':
        case '50-off-breach-discount-govan50':
        case '50-off-breach-discount':
            breachPackages['50-off-breach-discount'].forEach((pack) => {
                packages.push(pack)
            })
            break

        case '30-off-breach-discount-biked130':
            breachPackages['30-off-breach-discount-biked130'].forEach(
                (pack) => {
                    packages.push(pack)
                },
            )
            break

        case '50-off-afl-breach-screen':
            breachPackages['50-off-afl-breach-screen'].forEach(
                (pack) => {
                    packages.push(pack)
                },
            )
            break

        default:
            breachPackages[type].forEach((pack) => {
                packages.push(pack)
            })
            break
    }

    return packages
}

export function getWeeklyPrice(rateCode: string): number {
    switch (rateCode) {
        case WEEKLY_DIGITAL_RATE_CODE:
            return EVERYDAY_DIGITAL
        case WE_PAPER_EVERYDAY_DIGITAL_RATE_CODE:
            return WEEKEND_PAPERS_AND_EVERYDAY_DIGITAL
        case FIFTY_OFF_EVERYDAY_DIGITAL_12_WEEKS_RATE_CODE:
            return FIFTY_OFF_EVERYDAY_DIGITAL
        case FIFTY_OFF_FRI_MON_PAPER_AND_DIGITAL_12_WEEKS_RATE_CODE:
            return FIFTY_OFF_FRI_TO_MON_PAPERS_AND_EVERYDAY_DIGITAL
        default:
            return 0
    }
}

export function useUpdateBreachWallContext({
    publication,
    breachCreativeName,
    breachDeal,
    leadDepth,
    offerCode,
    packagePath,
    requiredAccessLevel,
}: BreachContext) {
    const breachScreenContext = useContext(BreachScreenContext)

    const authentication = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )

    const { entitlements, isLoggedIn } = authentication

    // a returning user is a user who has registered, logged in, and has not completed a purchase
    const returningUser = isLoggedIn && !entitlements?.includes(THEWEST_ALL)

    useEffect(() => {
        breachScreenContext.setContext({
            publication,
            returningUser,
            breachCreativeName,
            breachDeal,
            leadDepth,
            offerCode,
            packagePath,
            requiredAccessLevel,
        })
        return () => {
            breachScreenContext.clearContext()
        }

        // We can ignore the breachScreenContext dependency because the setContext and clearContext functions aren't going to change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        returningUser,
        publication,
        breachCreativeName,
        breachDeal,
        leadDepth,
        offerCode,
        packagePath,
        requiredAccessLevel,
    ])
}
